/**
 * Based on https://ui.shadcn.com/docs/components/button
 * Modified with improved TypeScript support and custom variants
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  [
    // Layout & Flex
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md',
    // Font
    "text-sm font-semibold font-['InterVariable','Inter_var',Inter,-apple-system,'system-ui','Helvetica_Neue','Segoe_UI',Oxygen,Ubuntu,Cantarell,'Open_Sans',sans-serif]",
    // Effects
    'transition-all',
    // Disabled
    'disabled:pointer-events-none disabled:opacity-50',
    // Cursor
    'cursor-pointer',
    // Focus
    'outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-white hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        primary:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        secondary: 'bg-muted text-foreground shadow-sm hover:bg-muted/90',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 py-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 py-3 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});

Button.displayName = 'Button';

export { Button, buttonVariants };
